import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Pure, UI-free proposal-PDF helpers shared by the AI BESS 360 tab (and formerly
// the standalone Proposal tab). Fetches an HTML template, substitutes {{vars}},
// drops unselected option columns, marks the recommended column, and opens a
// print-preview tab for Save-as-PDF. No React — safe to import anywhere.

// Template variable keys (must match the {{placeholders}} in
// public/templates/astern-proposal{,-cn,-my}.html). Option C kept in the list so
// older templates still substitute cleanly (it resolves to '' when not supplied).
export const PROPOSAL_VARS = [
    'customer_name', 'customer_phone', 'customer_address',
    'system_size_kwp', 'panel_count', 'panel_watt', 'monthly_generation_kwh', 'battery_capacity_kwh', 'bill_before',
    'option_a_kwp', 'option_a_panels', 'option_a_battery', 'option_a_inverter', 'option_a_bill_after', 'option_a_monthly_saving', 'option_a_payback_years', 'option_a_price', 'option_a_addons', 'option_a_cash_before_rebate', 'option_a_gov_rebate', 'option_a_cc_price',
    'option_b_kwp', 'option_b_panels', 'option_b_battery', 'option_b_inverter', 'option_b_bill_after', 'option_b_monthly_saving', 'option_b_payback_years', 'option_b_price', 'option_b_addons', 'option_b_cash_before_rebate', 'option_b_gov_rebate', 'option_b_cc_price',
    'option_c_kwp', 'option_c_panels', 'option_c_battery', 'option_c_inverter', 'option_c_bill_after', 'option_c_monthly_saving', 'option_c_payback_years', 'option_c_price', 'option_c_addons', 'option_c_cash_before_rebate', 'option_c_gov_rebate', 'option_c_cc_price',
    'cc_term',
    'date_issued', 'date_valid_until',
    'sales_person_name', 'sales_person_title', 'sales_person_email', 'sales_person_whatsapp',
    'company_website', 'company_social',
    'prepared_by_photo_url',
];

export const CURRENCY_VARS = new Set([
    'bill_before',
    'option_a_bill_after', 'option_b_bill_after', 'option_c_bill_after',
    'option_a_monthly_saving', 'option_b_monthly_saving', 'option_c_monthly_saving',
    'option_a_price', 'option_b_price', 'option_c_price',
    'option_a_cash_before_rebate', 'option_b_cash_before_rebate', 'option_c_cash_before_rebate',
    'option_a_gov_rebate', 'option_b_gov_rebate', 'option_c_gov_rebate',
    'option_a_cc_price', 'option_b_cc_price', 'option_c_cc_price',
]);

export function formatProposalValue(key, value) {
    if (!value) return '';
    if (CURRENCY_VARS.has(key)) {
        const n = parseFloat(String(value).replace(/[^\d.-]/g, ''));
        return Number.isFinite(n) ? n.toLocaleString('en-MY') : String(value);
    }
    if (key === 'prepared_by_photo_url' && value && String(value).startsWith('/')) {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');
        return `${apiBase}${value}`;
    }
    return String(value);
}

export function substitute(html, map) {
    let out = html;
    for (const key of PROPOSAL_VARS) {
        const v = formatProposalValue(key, map[key]);
        out = out.split(`{{${key}}}`).join(v);
    }
    return out;
}

// includes: [bool a, bool b, bool c]; recommendedOpt: 'a'|'b'|'c'|''; lang: 'en'|'cn'|'my'
export function postProcessProposal(html, { includes, recommendedOpt, lang, showCc = true }) {
    const count = includes.filter(Boolean).length;
    // NOTE: no "nothing to change" fast-path. The HTML template ships with a
    // 3-column comparison table (A/B/C), but the AI BESS 360 flow only ever
    // feeds A + B (includes = [true, true, false]). So the empty Option C column
    // must ALWAYS be dropped — returning the template untouched would leave a
    // blank 方案 C / Option C column in the PDF (bug fixed 2026-06-01).

    let doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); }
    catch (_) { return html; }

    if (count === 0) {
        const anchor = doc.querySelector('#p06');
        if (anchor) {
            const pageEl = anchor.nextElementSibling;
            anchor.remove();
            if (pageEl && pageEl.classList && pageEl.classList.contains('page')) pageEl.remove();
        }
        return '<!doctype html>\n' + doc.documentElement.outerHTML;
    }

    const table = doc.querySelector('.p8-table');

    // Cash-only proposals (no CC term chosen) drop the whole credit-card row.
    if (!showCc && table) {
        const ccRow = table.querySelector('tr.cc-row');
        if (ccRow) ccRow.remove();
    }

    if (table && count < 3) {
        const dropIdx = [];
        includes.forEach((on, i) => { if (!on) dropIdx.push(i + 1); });
        dropIdx.sort((a, b) => b - a).forEach(idx => {
            table.querySelectorAll('tr').forEach(tr => {
                const cell = tr.children[idx];
                if (cell) cell.remove();
            });
        });
    }

    if (table) {
        table.querySelectorAll('.recommended').forEach(el => el.classList.remove('recommended'));
        const remaining = ['a', 'b', 'c'].filter((_, i) => includes[i]);
        const recIdx = remaining.indexOf(recommendedOpt);
        if (recIdx >= 0) {
            const colIdx = recIdx + 1;
            const headerTh = table.querySelectorAll('thead tr th')[colIdx];
            if (headerTh) headerTh.classList.add('recommended');
            const priceRow = table.querySelector('tr.price-row');
            if (priceRow) {
                const priceCell = priceRow.children[colIdx];
                if (priceCell) priceCell.classList.add('recommended');
            }
        }
    }

    if (count < 3) {
        const title = doc.querySelector('.p8-title');
        const lede = doc.querySelector('.p8-lede');
        const isCn = lang === 'cn', isMy = lang === 'my';
        if (title) {
            title.innerHTML = isCn
                ? '投资从今天，<br><span class="accent">节省由此开始。</span>'
                : isMy
                ? 'Melabur hari ini —<br><span class="accent">jimat bermula sekarang.</span>'
                : 'Invest today —<br><span class="accent">savings start now.</span>';
        }
        if (lede) {
            lede.textContent = isCn
                ? '下方是我们为您挑选的配置。每月节省从安装之日起累积 — 越早投资，越早受益。'
                : isMy
                ? 'Di bawah ialah konfigurasi pilihan kami untuk anda. Penjimatan bulanan terkumpul dari hari pemasangan — lebih awal melabur, lebih awal menjimat.'
                : 'Below is the configuration we selected for you. Monthly savings accrue from installation day — the sooner you invest, the sooner you benefit.';
        }
    }

    return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

// Opens a print-preview tab; user picks Save-as-PDF for vector output.
// Returns { ok: true } or { ok: false, error } so the caller can surface it.
export async function openProposalPrintPreview(map, { lang, includes, recommendedOpt, showCc = true }) {
    try {
        const tplUrl = lang === 'cn' ? '/templates/astern-proposal-cn.html'
                     : lang === 'my' ? '/templates/astern-proposal-my.html'
                     : '/templates/astern-proposal.html';
        const res = await fetch(tplUrl);
        if (!res.ok) throw new Error(`Failed to fetch template (HTTP ${res.status})`);
        let tpl = await res.text();
        // Prod nginx returns the SPA index.html (200) for a missing static path.
        // The real template always contains class="page". If absent, fall back to EN.
        if (!/class="page"/.test(tpl)) {
            const enRes = await fetch('/templates/astern-proposal.html');
            const enTxt = enRes.ok ? await enRes.text() : '';
            if (/class="page"/.test(enTxt)) tpl = enTxt;
            else throw new Error('Proposal template not found on server.');
        }
        const origin = window.location.origin;
        tpl = tpl
            .split("src='./").join(`src='${origin}/templates/`)
            .split('src="./').join(`src="${origin}/templates/`)
            .split("url('./").join(`url('${origin}/templates/`)
            .split('url("./').join(`url("${origin}/templates/`)
            .split('url(./').join(`url(${origin}/templates/`);
        const populated = postProcessProposal(substitute(tpl, map), { includes, recommendedOpt, lang, showCc });
        const injection = `
<script>
(function(){
  function whenReady(cb){
    var imgs = Array.from(document.images || []);
    if (imgs.length === 0) { cb(); return; }
    var done = 0, total = imgs.length;
    imgs.forEach(function(img){
      if (img.complete && img.naturalWidth > 0) { if (++done === total) cb(); return; }
      img.addEventListener('load', function(){ if (++done === total) cb(); });
      img.addEventListener('error', function(){ if (++done === total) cb(); });
    });
    setTimeout(cb, 8000);
  }
  whenReady(function(){ setTimeout(function(){ try { window.print(); } catch(e){} }, 300); });
})();
<\/script>`;
        const finalHtml = populated.includes('</body>')
            ? populated.replace('</body>', injection + '</body>')
            : populated + injection;
        const blob = new Blob([finalHtml], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const printWin = window.open(url, '_blank');
        if (!printWin) {
            URL.revokeObjectURL(url);
            return { ok: false, error: 'Pop-up blocked. Please allow pop-ups for this site, then click again.' };
        }
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        return { ok: true };
    } catch (e) {
        return { ok: false, error: `Proposal failed: ${e.message || e}` };
    }
}

// Programmatic PDF (jsPDF + html2canvas) — a clean, downloadable PDF with NO browser
// print header/footer (no blob-URL / date / "Page X of Y") and a controlled file
// size. Mirrors generateCscProposalPdf: fetch the SAME template as the print path,
// substitute + postProcess, render into an offscreen iframe, snapshot each .page,
// and pdf.save(). Returns { ok } / { ok:false, error }.
export async function generateResidentialProposalPdf(map, { lang, includes, recommendedOpt, showCc = true }) {
    try {
        const tplUrl = lang === 'cn' ? '/templates/astern-proposal-cn.html'
                     : lang === 'my' ? '/templates/astern-proposal-my.html'
                     : '/templates/astern-proposal.html';
        let res = await fetch(`${tplUrl}?v=${Date.now()}`);
        let tpl = res.ok ? await res.text() : '';
        // Prod nginx returns the SPA index.html (200) for a missing static path;
        // the real template always contains class="page". Fall back to EN if absent.
        if (!/class="page"/.test(tpl)) {
            res = await fetch(`/templates/astern-proposal.html?v=${Date.now()}`);
            tpl = res.ok ? await res.text() : '';
            if (!/class="page"/.test(tpl)) throw new Error('Proposal template not found on server.');
        }
        const origin = window.location.origin;
        tpl = tpl
            .split("src='./").join(`src='${origin}/templates/`)
            .split('src="./').join(`src="${origin}/templates/`)
            .split("url('./").join(`url('${origin}/templates/`)
            .split('url("./').join(`url("${origin}/templates/`)
            .split('url(./').join(`url(${origin}/templates/`);
        const populated = postProcessProposal(substitute(tpl, map), { includes, recommendedOpt, lang, showCc });
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:794px;height:1123px;border:0;';
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open(); doc.write(populated); doc.close();
        if (doc.fonts && doc.fonts.ready) { try { await doc.fonts.ready; } catch {} }
        // Force-load the fonts actually used (Poppins body + Cocogoose brand) so
        // html2canvas rasterizes them instead of a fallback with broken glyphs.
        if (doc.fonts && doc.fonts.load) {
            try {
                await Promise.all([
                    doc.fonts.load('300 13pt Poppins'),
                    doc.fonts.load('400 13pt Poppins'),
                    doc.fonts.load('500 13pt Poppins'),
                    doc.fonts.load('600 13pt Poppins'),
                    doc.fonts.load('700 13pt Poppins'),
                    doc.fonts.load('800 24pt Poppins'),
                    doc.fonts.load('400 24pt Cocogoose'),
                ]);
            } catch {}
        }
        const imgs = Array.from(doc.images || []);
        await Promise.all(imgs.map((img) => {
            if (img.complete && img.naturalWidth > 0) return Promise.resolve();
            return new Promise((resolve) => {
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
                setTimeout(() => resolve(), 8000);
            });
        }));
        await new Promise((r) => setTimeout(r, 600));
        const pages = doc.querySelectorAll('.page');
        if (!pages.length) throw new Error('Template has no .page elements.');
        // compress + JPEG 0.92 keeps the file small (browser-print builds hit ~40 MB).
        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
        for (let i = 0; i < pages.length; i++) {
            const canvas = await html2canvas(pages[i], { scale: 2.5, useCORS: true, logging: false, backgroundColor: '#ffffff', imageTimeout: 15000 });
            const img = canvas.toDataURL('image/jpeg', 0.92);
            if (i > 0) pdf.addPage();
            pdf.addImage(img, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
        }
        const safeName = String(map.customer_name || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
        const kwp = String(map.system_size_kwp || '').trim();
        pdf.save(`${safeName}${kwp ? ' - ' + kwp + 'kWp' : ''} Astern Residential Proposal.pdf`);
        document.body.removeChild(iframe);
        return { ok: true };
    } catch (e) {
        console.error(e);
        return { ok: false, error: `Proposal failed: ${e.message || e}` };
    }
}

// optionA/optionB: seqOptions entries or null. customer: { name, phone, address,
// salesName, salesTitle, salesEmail, salesWhatsapp, preparedByPhotoUrl }.
// billBefore: pre-solar RM (number). invKwOf: (pkg) => number. dates: { issued, validUntil }.
export function buildProposalVarMapFromOptions({ optionA, optionB, customer, billBefore, invKwOf, dates, recommendedOpt = 'a', calcGovRebate, promoGovRebate = false, phaseLabel = '', ccFromCash, ccTerm = 36 }) {
    const map = {
        customer_name: customer.name || '',
        customer_phone: customer.phone || '',
        customer_address: customer.address || '',
        sales_person_name: customer.salesName || '',
        sales_person_title: customer.salesTitle || '',
        sales_person_email: customer.salesEmail || '',
        sales_person_whatsapp: customer.salesWhatsapp || '',
        prepared_by_photo_url: customer.preparedByPhotoUrl || '',
        date_issued: dates?.issued || '',
        date_valid_until: dates?.validUntil || '',
        bill_before: billBefore > 0 ? String(Math.round(billBefore)) : '',
        // CC term (months) shown in the credit-card row label. Empty → cash-only, the
        // whole CC row gets dropped in postProcessProposal (showCc=false).
        cc_term: (typeof ccFromCash === 'function' && ccTerm > 0) ? String(ccTerm) : '',
    };
    // Cover "system size" reflects the RECOMMENDED/selected option (not always A).
    const refOpt = recommendedOpt === 'b' ? (optionB || optionA) : (optionA || optionB);
    const refPkg = refOpt?.pkg || optionA?.pkg || optionB?.pkg;
    if (refPkg) {
        map.system_size_kwp = String(refPkg.kwp || '');
        map.panel_count = String(refPkg.panels || '');
        // Real panel wattage, not a fixed 640 (user 2026-08-20). The pricing snapshot
        // carries panelWatt; derive it from kWp / panels when absent, exactly as the
        // calculator and the WhatsApp message already do. The three RSC templates used
        // to hardcode "640W", so a 620W or 650W list still printed 640 to the customer.
        map.panel_watt = String(Number(refPkg.panelWatt)
            || (Number(refPkg.panels) > 0 ? Math.round(Number(refPkg.kwp) * 1000 / Number(refPkg.panels)) : 640));
        map.battery_capacity_kwh = String(refPkg.battKwhTotal || '');
        // monthlyGen ~ kWp * 3.5 * 30 (same yield as the calculator's calcSaving).
        map.monthly_generation_kwh = refPkg.kwp ? String(Math.round(refPkg.kwp * 3.5 * 30)) : '';
    }
    const put = (letter, opt) => {
        if (!opt || !opt.pkg) return;
        const pkg = opt.pkg;
        const saving = Number(pkg.savingMo) || 0;
        const billAfter = Math.max(0, (billBefore || 0) - saving);
        map[`option_${letter}_kwp`] = String(pkg.kwp || '');
        map[`option_${letter}_panels`] = pkg.panels != null ? String(pkg.panels) : '';
        map[`option_${letter}_battery`] = pkg.battKwhTotal != null ? String(pkg.battKwhTotal) : '';
        // Inverter cell: phase-prefixed kW, e.g. "1P 5kW" / "3P 8kW" (falls back to
        // bare kW if no phase label supplied). Empty when kW can't be resolved.
        const invKw = invKwOf(pkg);
        map[`option_${letter}_inverter`] = invKw
            ? `${phaseLabel ? phaseLabel + ' ' : ''}${invKw}kW`
            : '';
        map[`option_${letter}_bill_after`] = String(Math.round(billAfter));
        map[`option_${letter}_monthly_saving`] = String(Math.round(saving));
        map[`option_${letter}_payback_years`] = opt.roi != null ? String(opt.roi) : '';
        // cashP is the NETT cash (after the SuRIA rebate) — the bottom-line investment.
        // Surface the breakdown: final cash price → −SuRIA rebate → nett investment.
        const nett = Math.round(Number(opt.cashP) || 0);
        const govRebate = (promoGovRebate && typeof calcGovRebate === 'function')
            ? Math.round(calcGovRebate(invKwOf(pkg)) || 0) : 0;
        map[`option_${letter}_price`] = String(nett);
        map[`option_${letter}_gov_rebate`] = String(govRebate);
        map[`option_${letter}_cash_before_rebate`] = String(nett + govRebate);
        // Credit-card price mirrors the RSC option card EXACTLY: ccFromCash of the
        // pre-rebate cash (incl add-ons), WITHOUT the SuRIA rebate — the merchant
        // doesn't honour it (customer claims the 3k from TNB after install). Sits
        // right under Final Cash Price, before the rebate line. Only when a CC term
        // is actually selected (cash-only → row dropped, no cc_price).
        if (typeof ccFromCash === 'function' && ccTerm > 0) {
            map[`option_${letter}_cc_price`] = String(Math.round(ccFromCash(nett + govRebate, ccTerm) || 0));
        }
        // Add-on item names (no prices — folded into the total). Dash when none.
        map[`option_${letter}_addons`] = (opt.addonsLabel && String(opt.addonsLabel).trim()) ? String(opt.addonsLabel).trim() : '—';
    };
    put('a', optionA);
    put('b', optionB);
    return map;
}
